import { useState, useEffect, useMemo } from 'react';
import { settingsAPI } from '../services/api';
import { useSettingsContext } from './useSettings';
import { VOCAB_SEEDS } from '../constants/vocabSeeds';
import { isDeactivatedStatus, deactivatedUserIds } from '../utils/userStatus';

/**
 * useSalespersonOptions — shared hook for the salesperson picker.
 *
 * Fetches the tenant's user list (via settingsAPI.getUsers), filters to users
 * whose role's `can_sell` bit is true per the canonical `user_role.v1` seed,
 * merges with any legacy `salespersons` Settings list rows, deduplicates by
 * lowercased label, and returns SelectMenu/TypeaheadChips-shaped options.
 *
 * ─── Why this exists ─────────────────────────────────────────────────────
 * Previously, CustomerFormPanel.js and AddCustomerDrawer.js each carried a
 * verbatim copy of this logic with a HARDCODED 5-string inclusion allow-list:
 *   ['owner', 'admin', 'editor', 'salesperson', 'sales_rep']
 *
 * Two problems with that:
 *   1. It missed `office_admin` — the SOT (user_role.v1, custom:false,
 *      operator ruling §7 2026-07-21) says office admins CAN sell, but the
 *      old filter silently excluded them. This hook FIXES that bug.
 *   2. The list was maintained in two places (two files, verbatim). Adding
 *      or renaming a role required editing three places (seed + both files)
 *      or it would silently omit the new role. This hook eliminates that.
 *
 * Now the ONLY source of the "who can be tagged as salesperson" decision is
 * `vocabSeeds.user_role.v1.values[*].permissions.can_sell`. Adding a new role
 * with `can_sell:true` makes it appear in the picker on next render — no
 * consumer edits.
 *
 * ─── API ────────────────────────────────────────────────────────────────
 *
 * @param {object} [opts]
 * @param {boolean} [opts.enabled=true]
 *        Preserves the AddCustomerDrawer optimization: gate the fetch on the
 *        drawer's `open` state so a closed drawer doesn't burn a request.
 *        CustomerFormPanel omits this (always enabled — form is either
 *        rendered or not).
 * @returns {{
 *   options: Array<{value: string, label: string}>,
 *   loading: boolean,
 *   users:   Array<any>
 * }}
 */
export default function useSalespersonOptions({ enabled = true } = {}) {
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getOptions } = useSettingsContext();

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    setLoading(true);
    settingsAPI.getUsers()
      .then((res) => {
        if (ignore) return;
        // settingsAPI.getUsers already normalizes; this belt-and-suspenders
        // covers a hypothetical mock or a future API-shape change.
        const arr = Array.isArray(res) ? res
          : (Array.isArray(res?.data) ? res.data
            : (Array.isArray(res?.users) ? res.users : []));
        setSystemUsers(arr);
      })
      .catch(() => { if (!ignore) setSystemUsers([]); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [enabled]);

  const options = useMemo(() => {
    // Sellable-roles allow-list is DERIVED from the canonical seed. Adding a
    // new role with `can_sell:true` requires zero consumer edits.
    const sellableRoles = new Set(
      (VOCAB_SEEDS.user_role?.values || [])
        .filter((r) => r.permissions?.can_sell === true)
        .map((r) => r.value)
    );

    const fromUsers = (systemUsers || [])
      .filter((u) => {
        // IDI-681 D1: list_users reports a deactivated login as "inactive".
        if (isDeactivatedStatus(u.status)) return false;
        const role = (u.role || '').toLowerCase();
        // `!u.role` fallback preserves pre-seed users that have no role
        // assigned yet — they show up in the picker until they get a role.
        // A future PR could tighten this once every tenant user has a role.
        return sellableRoles.has(role) || !u.role;
      })
      // IDI-138: `value` must be a STABLE id (the Cognito username/sub),
      // not the display name. Storing a name in the id slot meant a later
      // rename silently orphaned every customer that had already tagged
      // that person -- their name would go on displaying the OLD value
      // forever, because nothing re-resolves a name to a person. Legacy
      // `settings/salespersons` rows (below) have no stable id at all, so
      // they still fall back to their label -- that's an accepted gap for
      // that specific legacy list, not reintroduced for system users who DO
      // have a real id.
      .map((u) => ({ value: u.id || u.name || u.email, label: u.name || u.email }));

    // Legacy `settings/salespersons` merge — retained for backward-compat
    // during the transition. See ledger item "Salesperson picker merges
    // legacy settings/salespersons list" for the retirement plan (safe to
    // drop once verified no tenant has meaningful rows in that list_type).
    const legacy = getOptions('salespersons') || [];

    const seen = new Set();
    const out = [];
    for (const opt of [...fromUsers, ...legacy]) {
      const key = (opt.label || opt.value || '').toString().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(opt);
    }
    return out;
  }, [systemUsers, getOptions]);

  const deactivatedIds = useMemo(() => deactivatedUserIds(systemUsers), [systemUsers]);

  return { options, loading, users: systemUsers, deactivatedIds };
}
